import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useGameStore } from '@/stores/game.store';
import { LootFileEditor } from '@/components/config/lootbox/LootFileEditor';
import { BoxKeysPanel } from '@/components/config/lootbox/BoxKeysPanel';
import { LOOT_FORMATS, LootKind, MAX_COUNT } from '@/components/config/lootbox/lootFormat';
import styles from './LootboxPage.module.scss';

// ============================================================
// Lootbox / Box : les deux types de fichiers de récompenses de PremiumMod
// ============================================================
// - Box : ouverte par une CLÉ (Paladium pour l'instant), data/mods/PremiumMod/LootBox/*.json
//   { Id, Loots }. L'onglet liste aussi les clés de la box et sait en créer (groupe de packs).
// - Lootbox : sans clé (NationsGlory), data/mods/PremiumMod/LootBoxes/*.json { Id, Rewards, Count } ;
//   Count tirages, objets lâchés devant le joueur, animation choisie par la rareté tirée.
// Même éditeur de récompenses pour les deux, chacun sur sa clé de tableau : le serveur borne les
// clés écrites à la liste blanche du type. Les deux onglets restent montés une fois ouverts, pour ne
// pas perdre une édition en cours en changeant d'onglet.
// ============================================================

const TABS: Array<{ kind: LootKind; label: string }> = [
  { kind: 'box', label: 'Box (à clés)' },
  { kind: 'lootbox', label: 'Lootbox (sans clé)' },
];

export function LootboxPage() {
  const activeGameId = useGameStore((s) => s.activeGameId);
  const [params, setParams] = useSearchParams();
  const asked = params.get('type');
  // Sans choix dans l'URL : le type du projet (box sur Paladium, lootbox sur NationsGlory).
  const tab: LootKind = asked === 'box' || asked === 'lootbox' ? asked : activeGameId === 'nationsglory' ? 'lootbox' : 'box';
  const [mounted, setMounted] = useState<LootKind[]>([tab]);

  useEffect(() => {
    setMounted((m) => (m.includes(tab) ? m : [...m, tab]));
  }, [tab]);

  const select = (kind: LootKind) => {
    setParams((p) => {
      const next = new URLSearchParams(p);
      next.set('type', kind);
      return next;
    }, { replace: true });
  };

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>Lootbox / Box</h1>
          <p className={styles.subtitle}>
            Éditer le contenu des box à clés et des lootbox (poids, rareté, récompenses) sans toucher au JSON, une
            fois pour tous les containers du projet. Après enregistrement, envoie les containers avec le bouton
            Envoyer pour l'appliquer en jeu.
          </p>
        </div>
      </header>

      <nav className={styles.tabBar}>
        {TABS.map((t) => (
          <button key={t.kind} type="button" className={`${styles.tabBtn} ${tab === t.kind ? styles.tabBtnOn : ''}`}
            onClick={() => select(t.kind)}>
            {t.label}
          </button>
        ))}
      </nav>

      {mounted.includes('box') && (
        <div className={styles.tabPane} hidden={tab !== 'box'}>
          <LootFileEditor
            format={LOOT_FORMATS.box}
            intro={(
              <>
                Box ouverte avec une clé (Paladium) : fichiers {LOOT_FORMATS.box.folder}/*.json, tableau « Loots ». Le
                nombre de loots de chaque grade se règle dans Supervision &gt; Grades &gt; Boutique (« Nombre de loots
                (box) »). Les clés sont des objets du pack, listées et créées sous l'en-tête de la box.
              </>
            )}
            renderExtras={(savedId) => <BoxKeysPanel boxId={savedId} />}
          />
        </div>
      )}

      {mounted.includes('lootbox') && (
        <div className={styles.tabPane} hidden={tab !== 'lootbox'}>
          <LootFileEditor
            format={LOOT_FORMATS.lootbox}
            intro={(
              <>
                Lootbox sans clé (NationsGlory) : fichiers {LOOT_FORMATS.lootbox.folder}/*.json, tableau « Rewards ».
                « Tirages » (Count, 1 à {MAX_COUNT}) fixe le nombre de récompenses tirées ; les objets gagnés tombent
                devant le joueur et la rareté tirée choisit l'animation (common 1, rare 2, epic 3, legendary 4). Pas de
                nombre de loots par grade pour les lootbox.
              </>
            )}
          />
        </div>
      )}
    </div>
  );
}
